import {
  mergeApiScopeRequirements,
  mergePermissionScopes,
} from 'utils/permissions/mergeAccessRestrictions';
import type { RoutesTreeItem } from './RoutesTreeItem';

const mergeFlags = (
  a: boolean | undefined,
  b: boolean | undefined,
): boolean | undefined =>
  a === undefined && b === undefined ? undefined : !!(a || b);

const mergeRouteItems = (
  a: RoutesTreeItem,
  b: RoutesTreeItem,
): RoutesTreeItem => {
  if (a.apiEndpoint && b.apiEndpoint && a.apiEndpoint !== b.apiEndpoint) {
    throw new Error(
      `Cannot merge route "${a.path}": ambiguous apiEndpoint "${a.apiEndpoint}" and "${b.apiEndpoint}".`,
    );
  }

  const merged: RoutesTreeItem = { ...a, ...b };

  const requiredApiScope = mergeApiScopeRequirements(
    a.requiredApiScope,
    b.requiredApiScope,
  );
  if (requiredApiScope !== undefined) {
    merged.requiredApiScope = requiredApiScope;
  }

  const requiredAuth = mergeFlags(a.requiredAuth, b.requiredAuth);
  if (requiredAuth !== undefined) {
    merged.requiredAuth = requiredAuth;
  }

  const requiredLicense = mergeFlags(a.requiredLicense, b.requiredLicense);
  if (requiredLicense !== undefined) {
    merged.requiredLicense = requiredLicense;
  }

  const requiredSnapshot = mergeFlags(a.requiredSnapshot, b.requiredSnapshot);
  if (requiredSnapshot !== undefined) {
    merged.requiredSnapshot = requiredSnapshot;
  }

  const requiredPermissions = mergePermissionScopes(
    a.requiredPermissions,
    b.requiredPermissions,
  );
  if (requiredPermissions !== undefined) {
    merged.requiredPermissions = requiredPermissions;
  }

  if (a.children && b.children) {
    merged.children = mergeRoutes([a.children, b.children]);
  }

  return merged;
};

export const mergeRoutes = (
  routes: [RoutesTreeItem[], RoutesTreeItem[]],
): RoutesTreeItem[] => {
  const [routesA, routesB] = routes;
  const mergedRoutes: RoutesTreeItem[] = [...routesA];

  for (const route of routesB) {
    // Pathless (index/layout) routes have no identity to merge on, so they are just added.
    const existingRoute =
      route.path === undefined
        ? undefined
        : mergedRoutes.find((mergedRoute) => mergedRoute.path === route.path);
    if (existingRoute === undefined) {
      mergedRoutes.push(route);
    } else {
      mergedRoutes[mergedRoutes.indexOf(existingRoute)] = mergeRouteItems(
        existingRoute,
        route,
      );
    }
  }

  return mergedRoutes;
};
